import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  absoluterPfad, breadcrumbPfad, type NavigationsEintrag,
} from '../navigation/struktur';
import './rahmen.css';

/**
 * Brotkrumen:
 *
 *   8000 – Munich Arena › Elektro › Nachweis je Raum
 *
 * "Breadcrumbs müssen echte Navigation sein. Nicht nur Text." Jede Stufe
 * außer der letzten ist deshalb ein Verweis; die letzte ist die aktuelle
 * Seite und trägt `aria-current="page"`.
 *
 * Die Kette entsteht aus denselben Einträgen wie die Seitenleiste
 * (`breadcrumbPfad` in `navigation/struktur.ts`) - es gibt keinen zweiten
 * Baum, der gepflegt werden müsste (Abschnitt 44).
 *
 * Bis zum 20.09.2026 stand die GRUPPE als Zwischenstufe mit drin
 * ("8000 – Munich Arena › Fachplanung › Elektro"). Sie ist entfallen: Seit
 * die Leiste nach Gewerken gegliedert ist, beantwortet das GEWERK die Frage
 * "in welchem Modul bin ich" - und zwar mit einem Namen, den es als Seite
 * wirklich gibt. Die Gruppe war reiner Text ohne Ziel und schob jede
 * Brotkrume um eine Stufe nach rechts.
 */
export interface BreadcrumbsProps {
  eintraege: NavigationsEintrag[];
  projektId?: string;
  /** Anzeigename des Projekts - die erste Stufe der Kette. */
  projektName?: string | null;
}

export function Breadcrumbs({ eintraege, projektId, projektName }: BreadcrumbsProps) {
  const { pathname } = useLocation();
  const kette = breadcrumbPfad(eintraege, projektId, pathname);

  /*
   * Das Projekt steht nur vor Seiten, die WIRKLICH zum Projekt gehören.
   *
   * Zustand, Aufträge, Maschinen, Installation und Modellvorgaben liegen
   * außerhalb von `/projekte/:id/` - sie gelten für den ganzen Container.
   * Am 17.09.2026 stand auf der Startseite "8000 – Munich Arena ›
   * Verwaltung › Zustand", und das behauptete eine Zugehörigkeit, die es
   * nicht gibt: Der Systemzustand gehört keinem Projekt.
   */
  const imProjekt = projektId !== undefined
    && pathname.startsWith(`/projekte/${projektId}`);
  const zeigeProjekt = imProjekt && !!projektName;

  // Ganz ohne Kette und ohne Projekt gibt es nichts zu zeigen. Ein leerer
  // Balken, der nur Platz wegnimmt, wäre schlimmer als keiner.
  if (kette.length === 0 && !zeigeProjekt) return null;

  const letzter = kette.length - 1;

  return (
    <nav className="brotkrumen" aria-label="Brotkrumen">
      {/* Der Trenner haengt IMMER am folgenden Glied, nie am vorangehenden.
          Sonst endet die Kette auf einer Modulseite ohne eigenen
          Navigationseintrag mit einem Pfeil ins Nichts ("8000 – Munich
          Arena ›") - am 17.09.2026 am Pruefstand genau so gesehen. */}
      {zeigeProjekt && (
        // Sehr lange Projektnamen werden gekürzt statt die Kette über die
        // ganze Breite zu ziehen; der volle Name steht im `title`.
        <Link
          to={`/projekte/${projektId}/uebersicht`}
          className="brotkrumen-projekt"
          title={projektName ?? undefined}
        >
          {projektName}
        </Link>
      )}

      {kette.map((eintrag, index) => (
        <Fragment key={eintrag.schluessel}>
          {(index > 0 || zeigeProjekt)
            && <span className="brotkrumen-trenner" aria-hidden="true">›</span>}
          {index === letzter
            ? (
              <span className="brotkrumen-aktuell" aria-current="page">
                {eintrag.beschriftung}
              </span>
            )
            : (
              <Link to={absoluterPfad(eintrag, projektId)}>{eintrag.beschriftung}</Link>
            )}
        </Fragment>
      ))}
    </nav>
  );
}
